import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import NotificationItem from "./NotificationItem";
import { FILTERS, dismissNotice, listInbox, markAllRead, markRead, markUnread } from "../../api/notificationInbox";
import { inboxChanged, refreshUnread } from "../../hook/useInboxUnread";
import { openNotice } from "../../utils/notificationLinks";

// The Notifications page (ticket 2.8.1): every notice, newest first, 20 at a time with "Load more".
// The filter (All, Unread, Licences, Rights, Wallet, Account) and how many pages are loaded live in the URL
// (?filter=unread&page=2), so a refresh or a shared link shows the same list.

const PAGE_SIZE = 20;
const MAX_PAGES = 10;
const FILTER_IDS = FILTERS.map((f) => f.id);

const EMPTY_TEXT = {
  all: "You have no notifications yet. Updates about your licences, rights cases, wallet and account will show up here.",
  unread: "You're all caught up. There is nothing unread.",
};

const Notificational = () => {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const rawFilter = params.get("filter");
  const filter = FILTER_IDS.includes(rawFilter) ? rawFilter : "all";
  const rawPage = parseInt(params.get("page"), 10);
  const pages = Number.isInteger(rawPage) && rawPage >= 1 ? Math.min(rawPage, MAX_PAGES) : 1;

  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ready | error
  const [loadingMore, setLoadingMore] = useState(false);
  const [moreError, setMoreError] = useState(false);
  const [unreadTotal, setUnreadTotal] = useState(0);
  const [unavailable, setUnavailable] = useState({});
  const requestId = useRef(0);

  const update = (next) => {
    const merged = new URLSearchParams(params);
    Object.entries(next).forEach(([key, value]) => {
      if (value === null || value === undefined || value === "" || (key === "filter" && value === "all") || (key === "page" && Number(value) <= 1)) merged.delete(key);
      else merged.set(key, String(value));
    });
    setParams(merged, { replace: true });
  };

  // Loads the first `count` pages for a filter (a refresh with ?page=3 brings back all three).
  const load = useCallback(async (which, count) => {
    const id = ++requestId.current;
    setStatus("loading");
    setMoreError(false);
    try {
      let all = [];
      let next = null;
      let unread = 0;
      for (let i = 0; i < count; i += 1) {
        // eslint-disable-next-line no-await-in-loop
        const result = await listInbox({ filter: which, limit: PAGE_SIZE, cursor: next });
        all = all.concat(result.items || []);
        next = result.next_cursor || null;
        unread = result.unread_count;
        if (!next) break;
      }
      if (id !== requestId.current) return;
      setItems(all);
      setCursor(next);
      setUnreadTotal(unread);
      setStatus("ready");
      inboxChanged(unread);
    } catch (e) {
      if (id === requestId.current) setStatus("error");
    }
  }, []);

  // Runs when the filter changes. The page count is read once per filter change so "Load more" does not reload the list.
  const pagesAtLoad = useRef(pages);
  pagesAtLoad.current = pages;
  useEffect(() => {
    load(filter, pagesAtLoad.current);
  }, [filter, load]);

  const loadMore = async () => {
    if (!cursor || loadingMore) return;
    setLoadingMore(true);
    setMoreError(false);
    try {
      const result = await listInbox({ filter, limit: PAGE_SIZE, cursor });
      setItems((list) => list.concat((result.items || []).filter((n) => !list.some((x) => x.id === n.id))));
      setCursor(result.next_cursor || null);
      setUnreadTotal(result.unread_count);
      inboxChanged(result.unread_count);
      update({ page: pages + 1 });
    } catch (e) {
      setMoreError(true);
    } finally {
      setLoadingMore(false);
    }
  };

  const changeFilter = (id) => {
    if (id === filter) return;
    const merged = new URLSearchParams(params);
    merged.delete("page");
    if (id === "all") merged.delete("filter");
    else merged.set("filter", id);
    setParams(merged, { replace: false });
  };

  const applyRead = (id, isRead, unread) => {
    setUnreadTotal(unread);
    setItems((list) => {
      if (filter === "unread" && isRead) return list.filter((n) => n.id !== id);
      return list.map((n) => (n.id === id ? { ...n, is_read: isRead } : n));
    });
    inboxChanged(unread);
  };

  const open = async (item) => {
    if (!item.is_read) {
      setItems((list) => list.map((n) => (n.id === item.id ? { ...n, is_read: true } : n)));
      markRead(item.id).then((r) => { setUnreadTotal(r.unread_count); inboxChanged(r.unread_count); }).catch(() => refreshUnread());
    }
    const result = await openNotice(item, navigate);
    if (!result.opened && result.message) setUnavailable((u) => ({ ...u, [item.id]: result.message }));
  };

  const toggleRead = async (item) => {
    const makeRead = !item.is_read;
    try {
      const r = makeRead ? await markRead(item.id) : await markUnread(item.id);
      applyRead(item.id, makeRead, r.unread_count);
    } catch (e) {
      toast.error("We couldn't update that notification. Please try again.");
    }
  };

  const dismiss = async (item) => {
    const before = items;
    setItems((list) => list.filter((n) => n.id !== item.id));
    try {
      const r = await dismissNotice(item.id);
      setUnreadTotal(r.unread_count);
      inboxChanged(r.unread_count);
    } catch (e) {
      setItems(before);
      toast.error("We couldn't dismiss that notification. Please try again.");
    }
  };

  const readAll = async () => {
    try {
      const r = await markAllRead();
      setUnreadTotal(0);
      setItems((list) => (filter === "unread" ? [] : list.map((n) => ({ ...n, is_read: true }))));
      inboxChanged(r.unread_count);
    } catch (e) {
      toast.error("We couldn't mark everything as read. Please try again.");
    }
  };

  const filterLabel = (FILTERS.find((f) => f.id === filter) || {}).label || "";

  return (
    <div className="w-full border bg-white dark:bg-gray-900">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b bg-white px-4 py-3 shadow-sm dark:border-gray-700 dark:bg-gray-900">
        <h1 className="text-base font-semibold text-gray-800 dark:text-white">
          Notifications{" "}
          {unreadTotal > 0 && (
            <span className="ml-1 rounded-full bg-green-500 px-2 py-0.5 text-[11px] font-bold text-white" aria-label={`${unreadTotal} unread`}>
              {unreadTotal > 9 ? "9+" : unreadTotal}
            </span>
          )}
        </h1>
        <Link
          to="/artist-studio/notifications"
          className="ml-auto rounded-md px-2 py-1 text-sm font-semibold text-gray-700 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-green-600"
          data-testid="notification-preferences-link"
        >
          Preferences
        </Link>
        <button
          type="button"
          onClick={readAll}
          disabled={unreadTotal === 0}
          className="rounded-md px-2 py-1 text-sm font-semibold text-green-700 hover:underline disabled:cursor-not-allowed disabled:text-gray-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-green-600"
        >
          Mark all as read
        </button>
      </div>

      <div role="group" aria-label="Filter notifications" className="flex gap-2 overflow-x-auto border-b border-gray-200 px-4 py-2 dark:border-gray-700">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            type="button"
            aria-pressed={filter === f.id}
            onClick={() => changeFilter(f.id)}
            className={`shrink-0 rounded-full px-3 py-1 text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-green-600 ${
              filter === f.id ? "bg-gray-900 text-white dark:bg-white dark:text-gray-900" : "bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-200"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {status === "loading" ? (
        <p className="p-4 text-sm text-gray-500" role="status">Loading...</p>
      ) : status === "error" ? (
        <div className="p-4 text-sm text-gray-600" role="alert">
          <p>We couldn't load your notifications.</p>
          <button type="button" onClick={() => load(filter, pages)} className="mt-2 rounded-md border border-gray-300 px-3 py-1 text-xs font-semibold text-gray-700 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-green-600">
            Try again
          </button>
        </div>
      ) : items.length === 0 ? (
        <p className="p-6 text-center text-sm text-gray-500">{EMPTY_TEXT[filter] || `No ${filterLabel.toLowerCase()} notifications.`}</p>
      ) : (
        <>
          <ul>
            {items.map((item) => (
              <NotificationItem key={item.id} item={item} onOpen={open} onToggleRead={toggleRead} onDismiss={dismiss} unavailable={unavailable[item.id]} />
            ))}
          </ul>
          {(cursor || moreError) && (
            <div className="p-4 text-center">
              {moreError && <p className="mb-2 text-sm text-gray-600" role="alert">We couldn't load more. Please try again.</p>}
              <button
                type="button"
                onClick={loadMore}
                disabled={loadingMore}
                className="rounded-md border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-green-600"
              >
                {loadingMore ? "Loading..." : "Load more"}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Notificational;
